// Sprint E · Task 3 — merge EEA + OSM industrial proximity into one section.
// EEA = official EU IED/E-PRTR (large regulated facilities, authoritative).
// OSM = OpenStreetMap (catches smaller crowdsourced facilities).
// Showing both side-by-side per overlap category lets analysts see the
// regulator-quality signal AND the granular signal in one place.
import { classifyDistance, bucketToColor } from './distanceThresholds';

// Categories that exist in BOTH datasets — render as merged pair.
// `thresholdKey` points into THRESHOLDS for color buckets.
// `field` points into FIELD_PROVENANCE for the ⓘ provenance popover.
export const OVERLAP_CATEGORIES = [
  { key: 'power_plant', label: 'Power generation',      eeaCat: 'power_plant', osmCat: 'power_plant',  thresholdKey: 'power_plant_distance_m', field: 'industrial_power_plant' },
  { key: 'industrial',  label: 'Industrial facility',   eeaCat: 'industrial',  osmCat: 'industrial',   thresholdKey: 'industrial_distance_m',  field: 'industrial_facility'    },
  { key: 'agriculture', label: 'Intensive agriculture', eeaCat: 'agriculture', osmCat: 'agricultural', thresholdKey: 'agriculture_distance_m', field: 'industrial_agriculture' },
];

// Categories ONLY in EEA.
export const EEA_ONLY_CATEGORIES = [
  { key: 'refinery', label: 'Petroleum refinery',      thresholdKey: 'refinery_distance_m', field: 'eea_refinery' },
  { key: 'chemical', label: 'Chemical / pharma plant', thresholdKey: 'chemical_distance_m', field: 'eea_chemical' },
];

// Categories ONLY in OSM.
export const OSM_ONLY_CATEGORIES = [
  { key: 'fuel_storage', label: 'Fuel storage',  thresholdKey: 'fuel_storage_distance_m', field: 'industrial_fuel_storage' },
  { key: 'gas_pipeline', label: 'Gas pipelines', thresholdKey: 'gas_pipeline_distance_m', field: 'industrial_gas_pipeline' },
  { key: 'wastewater',   label: 'Wastewater',    thresholdKey: 'wastewater_distance_m',   field: 'industrial_wastewater'   },
];

const TOLERANCE_M = 2000;

export function readEea(site, cat) {
  if (!site || site.eea_industrial_data_available === false) return null;
  const m = site[`eea_${cat}_distance_m`];
  if (m == null) return { m: null, kind: 'absent' };
  return {
    m,
    name:    site[`eea_${cat}_name`],
    company: site[`eea_${cat}_company`],
    isLcp:   !!site[`eea_${cat}_is_lcp`],
    warn:    !!site[`eea_${cat}_warn`],
    count5:  site[`eea_${cat}_count_5km`] || 0,
  };
}

export function readOsm(site, cat) {
  if (!site || site.proximity_data_available === false) return null;
  const m = site[`proximity_${cat}_distance_m`];
  if (m == null) return { m: null, kind: 'absent' };
  return {
    m,
    name:   site[`proximity_${cat}_name`],
    warn:   !!site[`proximity_${cat}_warn`],
    count2: site[`proximity_${cat}_count_2km`] || 0,
  };
}

// Decide how to render a paired EEA+OSM cell. Anything within TOLERANCE_M
// is considered "agreement" and collapsed.
export function mergePair(eea, osm) {
  const eeaHas = eea && eea.m != null;
  const osmHas = osm && osm.m != null;
  if (!eeaHas && !osmHas) {
    if (!eea && !osm) return { kind: 'no_data' };
    return { kind: 'none' };
  }
  if (!eeaHas) return { kind: 'osm_only', osm };
  if (!osmHas) return { kind: 'eea_only', eea };
  if (Math.abs(eea.m - osm.m) <= TOLERANCE_M) {
    return { kind: 'agree', m: Math.min(eea.m, osm.m), eea, osm };
  }
  return { kind: 'disagree', eea, osm };
}

export function fmtMeters(m) {
  if (m == null) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

// Worst-of-two bucket coloring driven by the documented THRESHOLDS for
// `thresholdKey` (e.g. 'industrial_distance_m'). Falls back to the binary
// backend warn flag if no threshold is registered for the category.
export function pairBucket(merged, thresholdKey) {
  if (merged.kind === 'no_data') return 'gray';
  if (merged.kind === 'none')    return 'green';
  const sides = [];
  if (merged.eea && merged.eea.m != null) sides.push(merged.eea);
  if (merged.osm && merged.osm.m != null) sides.push(merged.osm);
  if (sides.length === 0) return 'green';
  // Worst bucket wins. Order: bad > warn > good > neutral.
  const order = { bad: 3, warn: 2, good: 1, neutral: 0 };
  let worst = 'good';
  for (const s of sides) {
    const b = thresholdKey
      ? classifyDistance(thresholdKey, s.m)
      : (s.warn ? 'bad' : 'warn');
    if (order[b] > order[worst]) worst = b;
  }
  return bucketToColor(worst);
}

// Single-source bucket — used for EEA-only / OSM-only rows.
export function singleBucket(side, thresholdKey) {
  if (side == null) return 'gray';
  if (side.m == null) return 'green';
  if (thresholdKey) return bucketToColor(classifyDistance(thresholdKey, side.m));
  return side.warn ? 'red' : 'amber';
}
